import { useMemo, useState } from 'react';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';

import { useQueryStartDateChangesTableData } from 'api/queries/getTopStartDateChanges.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';

import { useMutationExportCsvReport } from 'components/Dashboards/Project/Components/VersionCompare/queries/tableDataMutation';
import { TKeys } from 'components/Dashboards/Project/Components/VersionCompare/utils/helper';
import { statusOptions as initialStatusOptions } from 'components/widgets/TopStartDateChanges/startDateChanges.utils';

export const useStartDateChangesProvider = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    localFilters,
    table,
}) => {
    const isInPinBoard = Boolean(widgetId);

    const [statusOptions, setStatusOptions] = useState<ISelectOption<string>[]>(initialStatusOptions);
    const [isCriticalPath, setIsCriticalPath] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<TKeys>(
        isInPinBoard && localFilters?.activeTab ? (localFilters?.activeTab as TKeys) : 'next',
    );

    const pinboardState = { ...localFilters, activeTab };
    const pageState = { ...globalFilterData, statusOptions, isCriticalPath, activeTab, sorting: table.sorting };

    const source = isInPinBoard ? pinboardState : pageState;

    const filters = useMemo(
        () =>
            getFiltersData({
                [FilterNames.ACTIVITY_CODES]: source?.activityCodes
                    ?.filter((item) => !item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                    ?.filter((item) => item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
                [FilterNames.WBS_SEARCH]: source?.wbs,
                [FilterNames.TAG_CARD_BOARD]: source?.tags
                    ?.filter((item) => !item.value.isExclude)
                    .map((i) => i.value.name),
                [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags
                    ?.filter((item) => item.value.isExclude)
                    .map((i) => i.value.name),
            }),
        [source.activityCodes, source.wbs, source.tags, source.milestoneFragnet],
    );

    const getExportCsvReportPayload = ({ filters }) => {
        return ({ reportType, fileName }) => ({
            compareVersionId: compareVersionId,
            contractId: contractId,
            filter: { filters },
            latestVersionId: latestVersionId,
            period: source.activeTab,
            projectId: projectId,
            reportType,
            fileName,
        });
    };

    return {
        ui: {
            state: getState({ source, compareVersionId, contractId, latestVersionId, projectId }),
            isInPinBoard: isInPinBoard,
            setters: {
                setIsCriticalPath,
                setStatusOptions,
                setActiveTab,
            },
            pills: useGetPills(source, widgetId, latestVersionId),
        },
        api: {
            getStartDateChanges: useQueryStartDateChangesTableData(
                getStartDateChangesPayload({ compareVersionId, contractId, latestVersionId, filters, projectId }),
            ),
            exportCsvReport: {
                instance: useMutationExportCsvReport(),
                createPayload: getExportCsvReportPayload({ filters }),
            },
        },
    };
};

const getState = ({ projectId, contractId, latestVersionId, compareVersionId, source }) => {
    return {
        // project
        projectId,
        contractId,
        latestVersionId,
        compareVersionId,

        // global Filter
        activityCodes: source?.activityCodes,
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags,
        wbs: source?.wbs,

        // local filters
        isCriticalPath: source?.isCriticalPath,
        statusOptions: source?.statusOptions,
        activeTab: source?.activeTab,
        sorting: source?.sorting,
    };
};

const getStartDateChangesPayload = ({ projectId, contractId, latestVersionId, compareVersionId, filters }) => ({
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
    filterData: {
        filters,
    },
});

const useGetPills = (source, widgetId, latestVersionId) => {
    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.value?.isExclude).map((i) => i.value?.name),
        tagsExclude: source?.tags?.filter((item) => item.value?.isExclude).map((i) => i.value?.name),
        wbs: source?.wbs,
        isCriticalPath: source?.isCriticalPath,
        statusFilter: source?.statusOptions?.map((item) => item.label),
    };

    const { pillsList: pills } = useGlobalFiltersHook({
        widgetId,
        latestVersionId,
        source: pillsData,
    });
    return pills;
};
